import { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import Chip from '@/components/ui/Chip.tsx';
import {
  BillChipVm,
  BillRowVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { cellClass, textCellClass } from './billRowStyles';

/** Pozycja zdjęta ze szkicu jest przekreślona, nie barwna — stąd brak tonu. */
const chipLook = (chip: BillChipVm): Partial<ComponentProps<typeof Chip>> => {
  if (chip.added) {
    return { tone: 'accent', fill: 'soft' };
  }
  return chip.removed ? { struck: true } : { tone: 'neutral', fill: 'soft' };
};

/** Wartość pozycji: pigułki wyboru wielokrotnego albo pojedynczy tekst. */
const ValueContent = ({ row }: { row: BillRowVm }) => {
  if (row.chips) {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        {row.chips.map((chip) => (
          <Chip key={chip.label} label={chip.label} {...chipLook(chip)} />
        ))}
      </span>
    );
  }

  if (!row.value) {
    return null;
  }

  return (
    <span
      className={cn(
        'text-[15px]',
        row.muted ? 'text-neutral-400' : 'font-semibold text-ink',
      )}
    >
      {row.value}
    </span>
  );
};

/**
 * Kolumna 2 — wartość i stan sprzed zmiany. `row.note` nie trafia na ekran:
 * to typ pola dla `resolveFieldType`, nie komunikat dla operatora.
 */
const ValueCell = ({ row }: { row: BillRowVm }) => (
  <div
    className={cn(
      cellClass,
      textCellClass,
      '@max-[640px]:col-start-1 @max-[640px]:row-start-2 @max-[640px]:pt-1 flex flex-wrap items-baseline gap-2.5',
    )}
  >
    <ValueContent row={row} />
    {row.oldValue && (
      <span className="text-sm text-neutral-400 line-through">
        {row.oldValue}
      </span>
    )}
    {/* `nowrap`: w wąskiej kolumnie dopisek schodzi pod wartość w całości,
        zamiast łamać się w pół daty. */}
    {row.selectedAt && (
      <span className="whitespace-nowrap text-xs text-neutral-500">
        {`wybrano ${formatDate(row.selectedAt)}`}
      </span>
    )}
  </div>
);

export default ValueCell;
